import React from 'react';
import styled from 'styled-components';
import { getTeamLogoUrl, getTeamAbbreviation } from '../../utils/teamLogos';

interface TeamLogoProps {
  teamName: string;
  size?: number;
  showAbbreviation?: boolean;
  className?: string;
}

const LogoContainer = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
`;

const LogoImage = styled.img<{ $size: number }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 2px;
`;

const LogoFallback = styled.div<{ $size: number }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => Math.max(8, props.$size * 0.3)}px;
  font-weight: bold;
  color: white;
  background: rgba(255, 255, 255, 0.1);
`;

const AbbreviationOverlay = styled.div<{ $size: number }>`
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: ${props => Math.max(6, props.$size * 0.15)}px;
  font-weight: bold;
  padding: 1px 3px;
  border-radius: 4px;
  min-width: 16px;
  text-align: center;
`;

export const TeamLogo: React.FC<TeamLogoProps> = React.memo(({ 
  teamName, 
  size = 32, 
  showAbbreviation = false,
  className 
}) => {
  const logoUrl = React.useMemo(() => getTeamLogoUrl(teamName), [teamName]);
  const abbreviation = React.useMemo(() => getTeamAbbreviation(teamName), [teamName]);
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = React.useCallback(() => {
    setImageError(true);
  }, []);

  return (
    <LogoContainer 
      $size={size} 
      className={className}
      role="img"
      aria-label={`${teamName} team logo`}
    >
      {logoUrl && !imageError ? (
        <>
          <LogoImage 
            src={logoUrl} 
            alt=""
            $size={size}
            onError={handleImageError}
            loading="lazy"
            decoding="async"
          />
          {showAbbreviation && (
            <AbbreviationOverlay $size={size} aria-hidden="true">
              {abbreviation}
            </AbbreviationOverlay>
          )}
        </>
      ) : (
        <LogoFallback $size={size} aria-label={`${teamName} team abbreviation`}>
          {abbreviation}
        </LogoFallback>
      )}
    </LogoContainer>
  );
});

TeamLogo.displayName = 'TeamLogo'; 